import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Toast } from './ui';

interface ToastMsg {
  id: number;
  message: string;
  kind: 'ok' | 'err';
}

interface ToastCtx {
  notify: (message: string, kind?: 'ok' | 'err') => void;
  clear: () => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);

  const notify = useCallback((message: string, kind: 'ok' | 'err' = 'ok') => {
    setToasts((prev) => [...prev.slice(-3), { id: Date.now() + Math.random(), message, kind }]);
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clear = useCallback(() => setToasts([]), []);

  return (
    <Ctx.Provider value={{ notify, clear }}>
      {children}
      {/* The same stack the customer surfaces use, announced to assistive
          technology; each toast leaves on its own. */}
      <div className="vx-toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <Toast key={t.id} message={t.message} kind={t.kind} onDone={() => remove(t.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
